import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Edit, ExternalLink, Eye, EyeOff, FileText, Loader2, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogTab = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', cover_image: '', tags: '', published: false });

  const fetchPosts = () => {
    setLoading(true);
    axios.get(`${API}/admin/blog/posts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPosts(); }, [token]);

  const resetForm = () => {
    setForm({ title: '', excerpt: '', content: '', cover_image: '', tags: '', published: false });
    setEditingPost(null);
    setShowEditor(false);
  };

  const handleEdit = (post) => {
    setForm({
      title: post.title,
      excerpt: post.excerpt || '',
      content: post.content || '',
      cover_image: post.cover_image || '',
      tags: (post.tags || []).join(', '),
      published: post.published
    });
    setEditingPost(post);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Post updated!');
      } else {
        await axios.post(`${API}/admin/blog/posts`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Post created!');
      }
      resetForm();
      fetchPosts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Post deleted');
      fetchPosts();
    } catch { toast.error('Failed to delete'); }
  };

  const handleTogglePublish = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/posts/${post.id}`, { ...post, published: !post.published, tags: (post.tags || []).join(', ') }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(post.published ? 'Post unpublished' : 'Post published!');
      fetchPosts();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-blog">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Blog Posts</h2>
          <p className="text-sm text-gray-500">{posts.length} post{posts.length !== 1 ? 's' : ''} total</p>
        </div>
        <button onClick={() => { resetForm(); setShowEditor(true); }}
          className="px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded flex items-center gap-2 hover:bg-gray-700"
          data-testid="new-post-btn">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-testid="blog-editor">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">{editingPost ? 'Edit Post' : 'New Blog Post'}</h3>
            <button onClick={resetForm} className="p-1 text-gray-400 hover:text-gray-900"><X className="w-5 h-5" /></button>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="e.g. 10 Best Movie Posters for Your Living Room" data-testid="blog-title" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Excerpt / Summary</label>
            <textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} rows={2}
              className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
              placeholder="A short description for SEO and preview cards..." data-testid="blog-excerpt" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Cover Image URL</label>
            <input value={form.cover_image} onChange={e => setForm({ ...form, cover_image: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="https://..." data-testid="blog-cover" />
            {form.cover_image && (
              <img src={form.cover_image} alt="Preview" className="mt-2 h-32 object-cover rounded" onError={e => e.target.style.display='none'} />
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">
              Content (HTML supported)
            </label>
            <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={12}
              className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 font-mono focus:outline-none focus:ring-2 focus:ring-gray-900 resize-y"
              placeholder="<h2>Introduction</h2><p>Write your article here...</p>" data-testid="blog-content" />
            <p className="text-xs text-gray-400 mt-1">Use HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;img&gt;, &lt;blockquote&gt;</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Tags (comma-separated)</label>
              <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                placeholder="wall art, home decor, tips" data-testid="blog-tags" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-gray-900" data-testid="blog-published" />
                <span className="text-sm text-gray-700 font-medium">Publish immediately</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving}
              className="px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
              data-testid="save-post-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {editingPost ? 'Update Post' : 'Create Post'}
            </button>
            <button onClick={resetForm} className="px-6 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Posts List */}
      {posts.length === 0 && !showEditor ? (
        <div className="bg-white border border-gray-200 rounded p-10 text-center">
          <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-bold text-gray-900 mb-1">No Blog Posts Yet</h3>
          <p className="text-sm text-gray-500 mb-4">Create your first post to start driving traffic to your store.</p>
          <button onClick={() => setShowEditor(true)} className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700">
            Create First Post
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div key={post.id} className="bg-white border border-gray-200 rounded p-4 flex items-center gap-4 hover:border-gray-300 transition-colors" data-testid={`blog-row-${post.id}`}>
              {post.cover_image ? (
                <img src={post.cover_image} alt="" className="w-20 h-14 object-cover rounded flex-shrink-0" />
              ) : (
                <div className="w-20 h-14 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-gray-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 truncate">{post.title}</p>
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${
                    post.published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>{post.published ? 'Published' : 'Draft'}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{(post.views || 0).toLocaleString()}</span>
                  {(post.tags || []).length > 0 && <span>{post.tags.join(', ')}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleTogglePublish(post)}
                  className={`p-2 rounded text-xs font-medium ${post.published ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                  title={post.published ? 'Unpublish' : 'Publish'}
                  data-testid={`toggle-publish-${post.id}`}>
                  {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => handleEdit(post)}
                  className="p-2 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                  data-testid={`edit-post-${post.id}`}>
                  <Edit className="w-4 h-4" />
                </button>
                <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-50">
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button onClick={() => handleDelete(post.id)}
                  className="p-2 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                  data-testid={`delete-post-${post.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SEO Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4">
        <h4 className="text-sm font-bold text-blue-900 mb-1">SEO Tips for Blog Posts</h4>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>Use keywords: <strong>wall art, posters, home decor, room decor, art prints</strong></li>
          <li>Write 500+ words per post for better search ranking</li>
          <li>Include internal links to your product pages</li>
          <li>Use descriptive tags for each post</li>
        </ul>
      </div>
    </div>
  );
};


export default BlogTab;
